import React, { useState, useEffect } from "react";
import { useSidebarContext } from "../scenes/global/sidebar/SidebarContext";
import { getAllAgreements } from "../api";
import printAgreement from "./AgreementPrint";
import {
  FileText,
  Search,
  User,
  MapPin,
  Building,
  Phone,
  IdCard,
  RefreshCw,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Clock,
  Printer,
  Filter
} from "lucide-react";
import { useSnackbar } from "notistack";
import { motion, AnimatePresence } from "framer-motion";

const AllAgreements = () => {
  const { activeSociety } = useSidebarContext();
  const { enqueueSnackbar } = useSnackbar();
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedId, setExpandedId] = useState(null);
  const itemsPerPage = 8;

  const fetchAgreements = async () => {
    setLoading(true);
    try {
      const response = await getAllAgreements(activeSociety?._id);
      setAgreements(response.agreements || []);
    } catch (error) {
      console.error("Error fetching agreements:", error);
      enqueueSnackbar("Failed to fetch agreements", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeSociety?._id) {
      fetchAgreements();
    }
  }, [activeSociety?._id]);

  const filteredAgreements = agreements.filter((agreement) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      agreement.customer?.name?.toLowerCase().includes(searchLower) ||
      agreement.plot?.plot_number?.toLowerCase().includes(searchLower) ||
      agreement.customer?.contactInfo?.phone?.includes(searchTerm) ||
      agreement.customer?.contactInfo?.cnic?.includes(searchTerm) ||
      agreement.booking_number?.toString().includes(searchTerm)
    );
  });

  const totalPages = Math.ceil(filteredAgreements.length / itemsPerPage);
  const currentAgreements = filteredAgreements.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-PK", {
      style: "currency",
      currency: "PKR",
      minimumFractionDigits: 0
    }).format(amount || 0).replace("PKR", "Rs.");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-PK", { day: "2-digit", month: "short", year: "numeric" });
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 pb-12 font-sans transition-colors duration-300">
      {/* Header section with Stats Bar */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-20 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-indigo-500 rounded-xl text-white shadow-lg shadow-indigo-500/30">
              <FileText size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800 dark:text-white leading-tight">Agreement Registry</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">{activeSociety?.name || "All Societies"}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex bg-slate-100 dark:bg-slate-700/50 p-1 rounded-lg">
              <div className="px-3 py-1 text-xs font-semibold text-slate-500 dark:text-slate-400 border-r border-slate-200 dark:border-slate-600">
                TOTAL: {agreements.length}
              </div>
              <div className="px-3 py-1 text-xs font-semibold text-indigo-600 dark:text-indigo-400">
                ACTIVE: {agreements.filter(a => a.booking_status !== 'Cancelled').length}
              </div>
            </div>
            <button
              onClick={fetchAgreements}
              className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-colors"
              title="Refresh Data"
            >
              <RefreshCw size={20} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Search & Filter Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            <input
              type="text"
              placeholder="Search by Plot, Customer, CNIC or Ph#..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none shadow-sm transition-all"
            />
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-sm text-slate-600 dark:text-slate-300">
            <Filter size={18} />
            <span className="text-sm font-medium">Filters</span>
          </div>
        </div>

        {/* Data Registry Table */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-slate-50/50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700 text-[11px] font-bold text-slate-500 uppercase tracking-widest">
            <div className="col-span-1">Ref #</div>
            <div className="col-span-3">Customer & Identity</div>
            <div className="col-span-2">Plot Details</div>
            <div className="col-span-2 text-right">Total Price</div>
            <div className="col-span-2 text-right">Balance</div>
            <div className="col-span-2 text-center">Actions</div>
          </div>

          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {loading ? (
              <div className="py-20 flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mb-4"></div>
                <p className="text-sm text-slate-400">Loading agreements...</p>
              </div>
            ) : filteredAgreements.length === 0 ? (
              <div className="py-20 flex flex-col items-center justify-center text-slate-400">
                <FileText size={48} className="mb-4 opacity-20" />
                <p>No agreement records found.</p>
              </div>
            ) : (
              currentAgreements.map((agreement) => (
                <div key={agreement._id}>
                  {/* Main Row */}
                  <div
                    className={`grid grid-cols-12 gap-4 px-6 py-5 items-center hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors cursor-pointer ${expandedId === agreement._id ? 'bg-indigo-50/50 dark:bg-indigo-900/20' : ''}`}
                    onClick={() => toggleExpand(agreement._id)}
                  >
                    <div className="col-span-1">
                      <span className="text-xs font-mono font-bold text-slate-400">#{agreement.booking_number || '---'}</span>
                    </div>
                    <div className="col-span-3">
                      <div className="font-bold text-slate-800 dark:text-white truncate">{agreement.customer?.name}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded text-slate-500 dark:text-slate-400 font-medium">
                          {agreement.customer?.contactInfo?.cnic || 'No CNIC'}
                        </span>
                        <span className="text-[10px] text-slate-400">{agreement.customer?.contactInfo?.phone}</span>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <div className="flex items-center gap-1.5 font-bold text-indigo-600 dark:text-indigo-400">
                        <Building size={14} />
                        <span className="text-sm">{agreement.plot?.plot_number || 'N/A'}</span>
                      </div>
                      <div className="text-[10px] text-slate-400 mt-1">
                        {agreement.plot?.block || 'No Block'} • {agreement.plot?.size || agreement.plot?.marla + ' Marla'}
                      </div>
                    </div>
                    <div className="col-span-2 text-right">
                      <div className="text-sm font-bold text-slate-700 dark:text-slate-200">{formatCurrency(agreement.total_amount)}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5 capitalize">{agreement.payment_mode}</div>
                    </div>
                    <div className="col-span-2 text-right">
                      <div className={`text-sm font-bold ${agreement.remaining_balance > 0 ? 'text-rose-500' : 'text-emerald-500'}`}>
                        {formatCurrency(agreement.remaining_balance)}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5">Remaining</div>
                    </div>
                    <div className="col-span-2 flex justify-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => printAgreement({ agreement })}
                        className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-all"
                        title="Print Agreement"
                      >
                        <Printer size={18} />
                      </button>
                      <button
                        onClick={() => toggleExpand(agreement._id)}
                        className={`p-2 rounded-lg transition-all ${expandedId === agreement._id ? 'text-indigo-600 bg-indigo-100 dark:bg-indigo-900/50' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                        title="View Details"
                      >
                        {expandedId === agreement._id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  <AnimatePresence>
                    {expandedId === agreement._id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700"
                      >
                        <div className="p-6 grid md:grid-cols-3 gap-6">
                          {/* Customer Details */}
                          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                            <div className="flex items-center gap-2 mb-4">
                              <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                                <User size={16} className="text-indigo-600 dark:text-indigo-400" />
                              </div>
                              <h4 className="font-bold text-slate-800 dark:text-white text-sm">Customer Details</h4>
                            </div>
                            <div className="space-y-3 text-sm">
                              <div className="flex items-center gap-2">
                                <User size={14} className="text-slate-400" />
                                <span className="text-slate-600 dark:text-slate-300">{agreement.customer?.name}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <IdCard size={14} className="text-slate-400" />
                                <span className="text-slate-600 dark:text-slate-300">S/O: {agreement.customer?.contactInfo?.father_name || 'N/A'}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Phone size={14} className="text-slate-400" />
                                <span className="text-slate-600 dark:text-slate-300">{agreement.customer?.contactInfo?.phone || 'N/A'}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <IdCard size={14} className="text-slate-400" />
                                <span className="text-slate-600 dark:text-slate-300">CNIC: {agreement.customer?.contactInfo?.cnic || 'N/A'}</span>
                              </div>
                              <div className="flex items-start gap-2">
                                <MapPin size={14} className="text-slate-400 mt-0.5" />
                                <span className="text-slate-600 dark:text-slate-300">{agreement.customer?.contactInfo?.address || 'No Address'}</span>
                              </div>
                            </div>
                          </div>

                          {/* Plot Details */}
                          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                            <div className="flex items-center gap-2 mb-4">
                              <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                                <Building size={16} className="text-emerald-600 dark:text-emerald-400" />
                              </div>
                              <h4 className="font-bold text-slate-800 dark:text-white text-sm">Plot Details</h4>
                            </div>
                            <div className="space-y-3 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-400">Plot Number</span>
                                <span className="font-bold text-slate-700 dark:text-slate-200">{agreement.plot?.plot_number}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-400">Block</span>
                                <span className="font-medium text-slate-600 dark:text-slate-300">{agreement.plot?.block || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-400">Size</span>
                                <span className="font-medium text-slate-600 dark:text-slate-300">{agreement.plot?.size || agreement.plot?.marla + ' Marla'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-400">Category</span>
                                <span className="font-medium text-slate-600 dark:text-slate-300">{agreement.plot?.category || 'Standard'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-400">Booking Date</span>
                                <span className="font-medium text-slate-600 dark:text-slate-300">{formatDate(agreement.booking_date)}</span>
                              </div>
                              {agreement.payment_mode === 'Installment' && agreement.planEndDate && (
                                <div className="flex justify-between">
                                  <span className="text-slate-400">Plan End Date</span>
                                  <span className="font-medium text-indigo-600 dark:text-indigo-400">{formatDate(agreement.planEndDate)}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Financial Summary */}
                          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                            <div className="flex items-center gap-2 mb-4">
                              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                                <DollarSign size={16} className="text-amber-600 dark:text-amber-400" />
                              </div>
                              <h4 className="font-bold text-slate-800 dark:text-white text-sm">Financial Summary</h4>
                            </div>
                            <div className="space-y-3 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-400">Total Amount</span>
                                <span className="font-bold text-emerald-600">{formatCurrency(agreement.total_amount)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-400">Initial Payment</span>
                                <span className="font-medium text-slate-600 dark:text-slate-300">{formatCurrency(agreement.initial_payment)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-400">Amount Paid</span>
                                <span className="font-medium text-slate-600 dark:text-slate-300">{formatCurrency(agreement.total_paid)}</span>
                              </div>
                              <div className="flex justify-between pt-2 border-t border-slate-200 dark:border-slate-700">
                                <span className="text-slate-500 font-semibold">Remaining Balance</span>
                                <span className={`font-bold ${agreement.remaining_balance > 0 ? 'text-rose-500' : 'text-emerald-500'}`}>
                                  {formatCurrency(agreement.remaining_balance)}
                                </span>
                              </div>
                              {agreement.payment_mode === 'Installment' && (
                                <div className="mt-3 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <Clock size={14} className="text-indigo-500" />
                                      <span className="text-xs font-medium text-indigo-600 dark:text-indigo-400">
                                        Plan: {agreement.installment_years || 0}Y {agreement.installment_months || 0}M
                                      </span>
                                    </div>
                                    <div className="text-xs font-bold text-indigo-600 dark:text-indigo-400">
                                      {agreement.paid_installments || 0} / {agreement.total_installments || 0} Paid
                                    </div>
                                  </div>
                                  {agreement.total_installments > 0 && (
                                    <div className="mt-2 h-2 bg-indigo-200 dark:bg-indigo-900/50 rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-indigo-500 rounded-full transition-all"
                                        style={{ width: `${((agreement.paid_installments || 0) / (agreement.total_installments || 1)) * 100}%` }}
                                      />
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Pagination Controls */}
        {!loading && totalPages > 1 && (
          <div className="mt-8 flex justify-center items-center gap-4">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm"
            >
              <ChevronLeft size={20} />
            </button>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">{currentPage}</span>
              <span className="text-sm text-slate-400 font-medium">of</span>
              <span className="text-sm font-bold text-slate-700 dark:text-slate-200">{totalPages}</span>
            </div>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllAgreements;
